import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import {
  ACESFilmicToneMapping,
  LinearFilter,
  LinearMipmapLinearFilter,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  SRGBColorSpace,
  Scene,
  SphereGeometry,
  WebGLRenderer,
} from 'three';
import {
  X,
  Camera,
  Grid3x3,
  LayoutGrid,
  RotateCcw,
  Maximize2,
  Minimize2,
  Crosshair,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../../config';
import { panoramaTextureCache } from '../../services/panoramaTextureCache';

interface PanoramaViewerProps {
  imageUrl: string;
  onClose: () => void;
  inline?: boolean;
  onScreenshot?: () => void;
  onMultiViewScreenshot?: (count: 4 | 12) => void;
  onToggleGrid?: () => void;
  onResetView?: () => void;
  onToggleFullscreen?: () => void;
}

const MAX_RENDERERS = 4;
const rendererPool: WebGLRenderer[] = [];
let activeRendererCount = 0;

const getOptimalSuperSample = (renderer: WebGLRenderer): number => {
  try {
    const gl = renderer.getContext();
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const rendererName: string = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      if (rendererName.includes('Intel') || rendererName.includes('Apple M')) {
        return 1.5;
      }
    }
  } catch {}
  if (window.innerWidth > 2560) return 1.5;
  return 2.0;
};

const calculateSegments = (texWidth: number, texHeight: number): [number, number] => {
  const targetPixelsPerSegment = 4;
  const wSegs = Math.min(Math.ceil(texWidth / targetPixelsPerSegment), 2048);
  const hSegs = Math.min(Math.ceil(texHeight / targetPixelsPerSegment), 1024);
  return [wSegs, hSegs];
};

const acquireRenderer = (): WebGLRenderer | null => {
  if (rendererPool.length > 0) {
    activeRendererCount++;
    return rendererPool.pop()!;
  }

  if (activeRendererCount >= MAX_RENDERERS) {
    return null;
  }

  activeRendererCount++;
  const renderer = new WebGLRenderer({
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
    depth: false,
    stencil: false,
  });
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  return renderer;
};

const releaseRenderer = (renderer: WebGLRenderer) => {
  activeRendererCount = Math.max(0, activeRendererCount - 1);

  if (rendererPool.length < MAX_RENDERERS) {
    rendererPool.push(renderer);
  } else {
    renderer.dispose();
  }
};

export const PanoramaViewer: React.FC<PanoramaViewerProps> = ({ imageUrl, onClose, inline = false, onScreenshot, onMultiViewScreenshot, onToggleGrid, onResetView, onToggleFullscreen }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<WebGLRenderer | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const cameraRef = useRef<PerspectiveCamera | null>(null);
  const sphereRef = useRef<Mesh | null>(null);
  const frameIdRef = useRef<number>(0);
  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });
  const lonRef = useRef(0);
  const latRef = useRef(0);
  const targetLonRef = useRef(0);
  const targetLatRef = useRef(0);
  const targetFovRef = useRef(75);
  const lastTimeRef = useRef(0);
  const isDestroyedRef = useRef(false);
  const currentImageUrlRef = useRef(imageUrl);
  const isAnimatingRef = useRef(true);
  const lastInteractionRef = useRef(performance.now());
  const isVisibleRef = useRef(true);
  const requestRenderRef = useRef<() => void>(() => {});
  const mipmapsEnabledRef = useRef(false);
  const isCoastingRef = useRef(false);
  const coastSpeedLonRef = useRef(0);
  const coastSpeedLatRef = useRef(0);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCtrlHeld, setIsCtrlHeld] = useState(false);
  const [contextLost, setContextLost] = useState(false);
  const [rendererLimitReached, setRendererLimitReached] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) setIsCtrlHeld(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) setIsCtrlHeld(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let resizeObserver: ResizeObserver | null = null;
    let visibilityObserver: IntersectionObserver | null = null;
    isDestroyedRef.current = false;
    currentImageUrlRef.current = imageUrl;
    mipmapsEnabledRef.current = false;

    const cleanupScene = () => {
      cancelAnimationFrame(frameIdRef.current);
      resizeObserver?.disconnect();
      visibilityObserver?.disconnect();

      if (sphereRef.current) {
        sphereRef.current.geometry.dispose();

        const mat = sphereRef.current.material as MeshBasicMaterial;
        if (mat.map) {
          panoramaTextureCache.releaseTexture(currentImageUrlRef.current);
          mat.map = null as any;
        }
        mat.dispose();

        sceneRef.current?.remove(sphereRef.current);
        sphereRef.current = null;
      }

      if (rendererRef.current) {
        const canvas = rendererRef.current.domElement;
        canvas.removeEventListener('webglcontextlost', handleContextLost);
        canvas.removeEventListener('webglcontextrestored', handleContextRestored);

        if (container.contains(canvas)) {
          container.removeChild(canvas);
        }

        releaseRenderer(rendererRef.current);
        rendererRef.current = null;
      }

      sceneRef.current = null;
      cameraRef.current = null;
    };

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      console.warn('[Panorama] WebGL Context Lost');
      cancelAnimationFrame(frameIdRef.current);
      isAnimatingRef.current = false;
      setContextLost(true);
      toast.error('GPU 资源不足，正在恢复...');
    };

    const handleContextRestored = () => {
      console.log('[Panorama] WebGL Context Restored');
      setContextLost(false);
      cleanupScene();
      initScene(currentImageUrlRef.current);
      toast.success('全景预览已恢复');
    };

    const runAnimate = (renderer: WebGLRenderer, scene: Scene, camera: PerspectiveCamera) => {
      const animate = () => {
        if (isDestroyedRef.current) return;

        const now = performance.now();
        const dt = Math.min((now - lastTimeRef.current) / 1000, 0.1);
        lastTimeRef.current = now;

        const lerpFactor = 1 - Math.pow(0.001, dt);

        // 惯性滑行：松手后 lonRef/latRef 直接按速度移动并衰减
        if (isCoastingRef.current) {
          const coastDecay = Math.pow(0.000005, dt);
          coastSpeedLonRef.current *= coastDecay;
          coastSpeedLatRef.current *= coastDecay;
          lonRef.current += coastSpeedLonRef.current;
          latRef.current += coastSpeedLatRef.current;
          latRef.current = Math.max(-85, Math.min(85, latRef.current));
          targetLonRef.current = lonRef.current;
          targetLatRef.current = latRef.current;
          if (Math.abs(coastSpeedLonRef.current) < 0.001 && Math.abs(coastSpeedLatRef.current) < 0.001) {
            isCoastingRef.current = false;
          }
        } else if (isDraggingRef.current) {
          // 拖拽时用较快的 lerp 追赶，产生轻微滞后感
          const dragLerp = 1 - Math.pow(0.00001, dt);
          const prevLon = lonRef.current;
          const prevLat = latRef.current;
          lonRef.current += (targetLonRef.current - lonRef.current) * dragLerp;
          latRef.current += (targetLatRef.current - latRef.current) * dragLerp;
          latRef.current = Math.max(-85, Math.min(85, latRef.current));
          // 记录实际视觉速度，供松手后 coasting 使用
          coastSpeedLonRef.current = lonRef.current - prevLon;
          coastSpeedLatRef.current = latRef.current - prevLat;
        } else {
          lonRef.current += (targetLonRef.current - lonRef.current) * lerpFactor;
          latRef.current += (targetLatRef.current - latRef.current) * lerpFactor;
          latRef.current = Math.max(-85, Math.min(85, latRef.current));
        }

        if (camera.fov !== targetFovRef.current) {
          camera.fov += (targetFovRef.current - camera.fov) * lerpFactor;
          camera.updateProjectionMatrix();
        }

        const phi = MathUtils.degToRad(90 - latRef.current);
        const theta = MathUtils.degToRad(lonRef.current);

        const x = 500 * Math.sin(phi) * Math.cos(theta);
        const y = 500 * Math.cos(phi);
        const z = 500 * Math.sin(phi) * Math.sin(theta);

        camera.lookAt(x, y, z);
        renderer.render(scene, camera);

        const lonDiff = Math.abs(targetLonRef.current - lonRef.current);
        const latDiff = Math.abs(targetLatRef.current - latRef.current);
        const fovDiff = Math.abs(targetFovRef.current - camera.fov);
        const isConverged = lonDiff < 0.01 && latDiff < 0.01 && fovDiff < 0.01;
        const timeSinceInteraction = now - lastInteractionRef.current;

        if (isConverged && timeSinceInteraction > 1500) {
          isAnimatingRef.current = false;
          return;
        }

        frameIdRef.current = requestAnimationFrame(animate);
      };

      isAnimatingRef.current = true;
      lastTimeRef.current = performance.now();
      animate();
    };

    const requestRender = () => {
      lastInteractionRef.current = performance.now();
      if (isAnimatingRef.current || isDestroyedRef.current || !isVisibleRef.current) return;

      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      if (!renderer || !scene || !camera) return;

      runAnimate(renderer, scene, camera);
    };

    requestRenderRef.current = requestRender;

    const initScene = (rawUrl: string) => {
      if (isDestroyedRef.current) return;

      let url = rawUrl;
      if (url.startsWith(`${API_BASE}/assets/`)) {
        url = url.replace(API_BASE, '');
      }

      const width = container.clientWidth;
      const height = container.clientHeight;

      if (width === 0 || height === 0) {
        requestAnimationFrame(() => initScene(url));
        return;
      }

      const renderer = acquireRenderer();
      if (!renderer) {
        console.warn('[Panorama] Max renderers reached:', activeRendererCount);
        setRendererLimitReached(true);
        setIsLoading(false);
        return;
      }

      rendererRef.current = renderer;
      setRendererLimitReached(false);

      const superSampleRatio = getOptimalSuperSample(renderer);
      const pixelRatio = Math.min(window.devicePixelRatio, 2);
      renderer.setPixelRatio(pixelRatio * superSampleRatio);
      renderer.setSize(width, height, false);

      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      renderer.domElement.style.display = 'block';
      container.appendChild(renderer.domElement);

      renderer.domElement.addEventListener('webglcontextlost', handleContextLost, false);
      renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored, false);

      const scene = new Scene();
      sceneRef.current = scene;

      const camera = new PerspectiveCamera(75, width / height, 0.1, 1000);
      cameraRef.current = camera;
      targetFovRef.current = 75;

      setIsLoading(true);
      mipmapsEnabledRef.current = false;

      const loadTexture = async () => {
        try {
          const texture = await panoramaTextureCache.loadTexture(url, renderer, {
            timeout: 30000,
            retries: 3,
            retryDelay: 2000,
          });

          if (isDestroyedRef.current) {
            panoramaTextureCache.releaseTexture(url);
            return;
          }

          const img = texture.image as HTMLImageElement;
          const [wSegs, hSegs] = calculateSegments(img.width, img.height);

          const geometry = new SphereGeometry(500, wSegs, hSegs);
          geometry.scale(-1, 1, 1);

          const material = new MeshBasicMaterial({ map: texture });
          const sphere = new Mesh(geometry, material);
          scene.add(sphere);
          sphereRef.current = sphere;
          setIsLoading(false);

          console.log('[Panorama] Ready | Mode:', inline ? 'inline' : 'fullscreen', '| Segments:', wSegs + 'x' + hSegs, '| SuperSample:', superSampleRatio + 'x', '| Active:', activeRendererCount, '| Pool:', rendererPool.length, '| Cache:', panoramaTextureCache.getCacheSize());
        } catch (err) {
          if (isDestroyedRef.current) return;
          console.error('[Panorama] Texture load failed:', err);
          toast.error('全景图加载失败，请重试');
          setIsLoading(false);
        }
      };

      loadTexture();

      runAnimate(renderer, scene, camera);
    };

    initScene(imageUrl);

    visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = isVisibleRef.current;
        isVisibleRef.current = entry.isIntersecting;

        if (entry.isIntersecting && !wasVisible && !isAnimatingRef.current) {
          requestRender();
        }

        if (!entry.isIntersecting && isAnimatingRef.current) {
          cancelAnimationFrame(frameIdRef.current);
          isAnimatingRef.current = false;
        }
      },
      { threshold: 0.05 }
    );
    visibilityObserver.observe(container);

    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0) return;
      if (cameraRef.current) {
        cameraRef.current.aspect = w / h;
        cameraRef.current.updateProjectionMatrix();
      }
      if (rendererRef.current) {
        rendererRef.current.setSize(w, h, false);
      }
      requestRender();
    };

    window.addEventListener('resize', handleResize);

    resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(container);

    return () => {
      isDestroyedRef.current = true;
      cleanupScene();
      requestRenderRef.current = () => {};
      window.removeEventListener('resize', handleResize);
    };
  }, [imageUrl]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.ctrlKey || e.metaKey) return;
    isDraggingRef.current = true;
    isCoastingRef.current = false;
    coastSpeedLonRef.current = 0;
    coastSpeedLatRef.current = 0;
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
    lastInteractionRef.current = performance.now();
    requestRenderRef.current();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    const deltaX = e.clientX - previousMouseRef.current.x;
    const deltaY = e.clientY - previousMouseRef.current.y;

    // 拖拽灵敏度：调高使相同鼠标位移产生更大旋转角度，加快移动速度
    const sensitivity = 0.25;
    targetLonRef.current -= deltaX * sensitivity;
    targetLatRef.current += deltaY * sensitivity;
    targetLatRef.current = Math.max(-85, Math.min(85, targetLatRef.current));

    previousMouseRef.current = { x: e.clientX, y: e.clientY };
    lastInteractionRef.current = performance.now();
    requestRenderRef.current();
  }, []);

  const handlePointerUp = useCallback(() => {
    if (isDraggingRef.current) {
      // 启动惯性滑行，速度取自最后一次 move 的 delta
      isCoastingRef.current = true;
    }
    isDraggingRef.current = false;
    lastInteractionRef.current = performance.now();
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) return;
    if (!cameraRef.current) return;
    const zoomSpeed = 0.08;
    targetFovRef.current += e.deltaY * zoomSpeed;
    targetFovRef.current = Math.max(20, Math.min(120, targetFovRef.current));

    const texture = (sphereRef.current?.material as MeshBasicMaterial)?.map;
    if (texture) {
      if (targetFovRef.current > 90 && !mipmapsEnabledRef.current) {
        texture.generateMipmaps = true;
        texture.minFilter = LinearMipmapLinearFilter;
        texture.needsUpdate = true;
        mipmapsEnabledRef.current = true;
      } else if (targetFovRef.current <= 90 && mipmapsEnabledRef.current) {
        texture.generateMipmaps = false;
        texture.minFilter = LinearFilter;
        texture.needsUpdate = true;
        mipmapsEnabledRef.current = false;
      }
    }

    lastInteractionRef.current = performance.now();
    requestRenderRef.current();
  }, []);

  const takeScreenshot = useCallback(() => {
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    if (!renderer || !scene || !camera) return;

    const texture = (sphereRef.current?.material as MeshBasicMaterial)?.map;
    const wasMipmapsEnabled = mipmapsEnabledRef.current;

    if (texture && wasMipmapsEnabled) {
      texture.generateMipmaps = false;
      texture.minFilter = LinearFilter;
      texture.needsUpdate = true;
    }

    renderer.render(scene, camera);

    const link = document.createElement('a');
    link.download = `panorama-view-${Date.now()}.png`;
    try {
      link.href = renderer.domElement.toDataURL('image/png', 1.0);
      link.click();
      toast.success('当前视角截图已保存');
    } catch {
      toast.error('截图失败，可能受跨域限制');
    }

    if (texture && wasMipmapsEnabled) {
      texture.generateMipmaps = true;
      texture.minFilter = LinearMipmapLinearFilter;
      texture.needsUpdate = true;
    }
  }, []);

  const takeMultiViewScreenshot = useCallback((count: 4 | 12) => {
    if (!rendererRef.current || !cameraRef.current || !sceneRef.current) return;

    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const originalLon = targetLonRef.current;
    const originalLat = targetLatRef.current;
    const views = count === 4
      ? [0, 90, 180, 270]
      : [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

    const texture = (sphereRef.current?.material as MeshBasicMaterial)?.map;
    const wasMipmapsEnabled = mipmapsEnabledRef.current;

    if (texture && wasMipmapsEnabled) {
      texture.generateMipmaps = false;
      texture.minFilter = LinearFilter;
      texture.needsUpdate = true;
    }

    const captureView = (lon: number, lat: number, index: number) => {
      lonRef.current = lon;
      latRef.current = lat;
      targetLonRef.current = lon;
      targetLatRef.current = lat;

      const phi = MathUtils.degToRad(90 - lat);
      const theta = MathUtils.degToRad(lon);
      const x = 500 * Math.sin(phi) * Math.cos(theta);
      const y = 500 * Math.cos(phi);
      const z = 500 * Math.sin(phi) * Math.sin(theta);

      camera.lookAt(x, y, z);
      renderer.render(scene, camera);

      requestAnimationFrame(() => {
        const link = document.createElement('a');
        link.download = `panorama-${count}view-${index + 1}-${Date.now()}.png`;
        try {
          link.href = renderer.domElement.toDataURL('image/png', 1.0);
          link.click();
        } catch {
          console.warn('[Panorama] Screenshot failed for view', index);
        }
      });
    };

    views.forEach((lon, i) => {
      setTimeout(() => {
        captureView(lon, 0, i);
      }, i * 400);
    });

    setTimeout(() => {
      targetLonRef.current = originalLon;
      targetLatRef.current = originalLat;

      if (texture && wasMipmapsEnabled) {
        texture.generateMipmaps = true;
        texture.minFilter = LinearMipmapLinearFilter;
        texture.needsUpdate = true;
      }
    }, views.length * 400 + 200);

    toast.success(`${count}视角截图已开始下载`);
  }, []);

  const resetView = useCallback(() => {
    targetLonRef.current = 0;
    targetLatRef.current = 0;
    targetFovRef.current = 75;
    lastInteractionRef.current = performance.now();
    requestRenderRef.current();
    toast.success('视角已重置');
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (document.fullscreenElement) {
          document.exitFullscreen();
          setIsFullscreen(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className={`${inline ? `absolute inset-0 z-10 rounded-[16px] overflow-hidden${isCtrlHeld ? '' : ' nodrag nowheel'}` : 'fixed inset-0 z-[200]'} bg-black flex flex-col`}
    >
      {!inline && (
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center gap-1 p-3">
        <div className="flex items-center gap-1 bg-[#1c1c1e]/80 backdrop-blur-2xl border border-white/10 rounded-xl px-2 py-1.5 shadow-2xl">
          <button
            onClick={onClose}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-transparent text-white/70 hover:text-white hover:bg-white/10 transition-all"
            title="退出全景 (ESC)"
          >
            <X size={18} />
          </button>
          <div className="w-px h-5 bg-white/10 mx-1" />

          <button
            onClick={takeScreenshot}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-transparent text-white/70 hover:text-white hover:bg-white/10 transition-all"
            title="当前视角截图"
          >
            <Camera size={18} />
          </button>

          <button
            onClick={() => takeMultiViewScreenshot(4)}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-transparent text-white/70 hover:text-white hover:bg-white/10 transition-all"
            title="4视角截图"
          >
            <Grid3x3 size={18} />
          </button>

          <button
            onClick={() => takeMultiViewScreenshot(12)}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-transparent text-white/70 hover:text-white hover:bg-white/10 transition-all"
            title="12视角截图"
          >
            <LayoutGrid size={18} />
          </button>

          <div className="w-px h-5 bg-white/10 mx-1" />

          <button
            onClick={() => setShowGrid(!showGrid)}
            className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${
              showGrid ? 'bg-white/20 text-white' : 'bg-transparent text-white/70 hover:text-white hover:bg-white/10'
            }`}
            title="构图参考线"
          >
            <Crosshair size={18} />
          </button>

          <button
            onClick={resetView}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-transparent text-white/70 hover:text-white hover:bg-white/10 transition-all"
            title="重置视角"
          >
            <RotateCcw size={18} />
          </button>

          <button
            onClick={toggleFullscreen}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-transparent text-white/70 hover:text-white hover:bg-white/10 transition-all"
            title="全屏"
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>
      )}

      <div
        ref={containerRef}
        className="flex-1 w-full h-full cursor-grab active:cursor-grabbing relative"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onWheel={handleWheel}
      >
        {isLoading && !contextLost && !rendererLimitReached && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
              <span className="text-sm text-white/60">加载全景图中...</span>
            </div>
          </div>
        )}

        {contextLost && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-yellow-400/40 border-t-yellow-400 rounded-full animate-spin" />
              <span className="text-sm text-yellow-400/80">GPU 恢复中...</span>
            </div>
          </div>
        )}

        {rendererLimitReached && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
            <div className="flex flex-col items-center gap-3">
              <span className="text-sm text-white/50">GPU 资源已满，请关闭其他全景预览</span>
            </div>
          </div>
        )}

        {showGrid && !isLoading && !contextLost && !rendererLimitReached && (
          <div className="absolute inset-0 pointer-events-none z-10">
            <div className="absolute inset-0">
              <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/20" />
              <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/20" />
              <div className="absolute top-1/3 left-0 right-0 h-px bg-white/20" />
              <div className="absolute top-2/3 left-0 right-0 h-px bg-white/20" />
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 border border-white/30 rounded-full" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-white/10" />
          </div>
        )}
      </div>

      {!inline && (
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
        <div className="flex items-center gap-2 px-4 py-2 bg-[#1c1c1e]/60 backdrop-blur-xl border border-white/10 rounded-full">
          <span className="text-[11px] text-white/40">拖拽旋转 · 滚轮缩放 · ESC退出</span>
        </div>
      </div>
      )}
    </div>
  );
};
